import * as React from "react";
import styled, { css } from "styled-components";
import dateFormat from "date-fns/format";

import { PrimaryButton, LinkButton } from "../../common/components/buttons";
import Translate from "../../common/components/Translate";
import Icon from "../../common/components/Icon";
import { MonthOptions, YearOptions } from "../../common/components/DateOptions";

type Company = WtdModels.Partial<
  WtdModels.Company,
  "address_prefix" | "address_suffix" | "founded_on" | "font_color_code" | "payroll_number" | "url" | "founder"
>;

export interface EditBasicInfoModels {
  Company: Company;
}

interface Props {
  company: Company;
  onSubmit(company: Company): void;
  onCancel(): void;
}

interface StyleProps {
  color: string;
}

interface State {
  company: Company;
}

class EditBasicInfo extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      company: props.company,
    };
  }

  public updateCompany(attrs: Partial<Company>) {
    this.setState(({ company }) => ({
      company: {
        ...company,
        ...attrs,
      },
    }));
  }

  public onChangeAddressPrefix = (event: React.FormEvent<HTMLInputElement>) => {
    this.updateCompany({ address_prefix: event.currentTarget.value });
  };

  public onChangeAddressSuffix = (event: React.FormEvent<HTMLInputElement>) => {
    this.updateCompany({ address_suffix: event.currentTarget.value });
  };

  public onChangeUrl = (event: React.FormEvent<HTMLInputElement>) => {
    this.updateCompany({ url: event.currentTarget.value });
  };

  public onChangeFounder = (event: React.FormEvent<HTMLInputElement>) => {
    this.updateCompany({ founder: event.currentTarget.value });
  };

  public onChangeFoundedYear = (event: React.FormEvent<HTMLSelectElement>) => {
    const { founded_on } = this.state.company;
    const date = founded_on ? new Date(founded_on) : new Date();
    const year = Number(event.currentTarget.value);
    this.updateCompany({ founded_on: new Date(dateFormat(date.setFullYear(year))) });
  };

  public onChangeFoundedMonth = (event: React.FormEvent<HTMLSelectElement>) => {
    const { founded_on } = this.state.company;
    const date = founded_on ? new Date(founded_on) : new Date();
    const month = Number(event.currentTarget.value) - 1;
    this.updateCompany({ founded_on: new Date(dateFormat(date.setMonth(month))) });
  };

  public onChangePayrollNumber = (event: React.FormEvent<HTMLInputElement>) => {
    this.updateCompany({ payroll_number: Number(event.currentTarget.value) });
  };

  public onSubmit = (event: React.FormEvent<any>) => {
    event.preventDefault();
    this.props.onSubmit(this.state.company);
  };

  public onCancel = () => {
    this.props.onCancel();
  };

  public render() {
    const { company } = this.state;
    return (
      <Form onSubmit={this.onSubmit}>
        <Fieldset>
          <LabelIcon name="wt-icon-link" />
          <Label>URL</Label>
          {/* TODO(KentoMoriwaki): Validate URL format */}
          <Input
            color={company.font_color_code}
            type="text"
            value={company.url || ""}
            maxLength={255}
            required
            onChange={this.onChangeUrl}
          />
        </Fieldset>
        <Fieldset>
          <LabelIcon name="wt-icon-location" />
          <Label>
            <Translate id="company.address_prefix" />
          </Label>
          <Input
            color={company.font_color_code}
            type="text"
            value={company.address_prefix || ""}
            maxLength={255}
            onChange={this.onChangeAddressPrefix}
          />
        </Fieldset>
        <Fieldset>
          <LabelIcon name="wt-icon-location" />
          <Label>
            <Translate id="company.address_suffix" />
          </Label>
          <Input
            color={company.font_color_code}
            type="text"
            value={company.address_suffix || ""}
            maxLength={255}
            onChange={this.onChangeAddressSuffix}
          />
        </Fieldset>
        <Fieldset>
          <LabelFaIcon className="icon-flag" />
          <Label>
            <Translate id="company.founded" />
          </Label>
          <SelectField>
            <Select
              color={company.font_color_code}
              value={(company.founded_on && dateFormat(company.founded_on, "YYYY")) || ""}
              onChange={this.onChangeFoundedYear}
              required
            >
              <YearOptions />
            </Select>
            <DateSeparator>/</DateSeparator>
            <Select
              color={company.font_color_code}
              value={(company.founded_on && dateFormat(company.founded_on, "M")) || ""}
              onChange={this.onChangeFoundedMonth}
              required
            >
              <MonthOptions />
            </Select>
          </SelectField>
        </Fieldset>
        <Fieldset>
          <LabelIcon name="wt-icon-person" />
          <Label>
            <Translate id="company.founder" />
          </Label>
          <Input
            color={company.font_color_code}
            type="text"
            value={company.founder || ""}
            maxLength={255}
            onChange={this.onChangeFounder}
          />
        </Fieldset>
        <Fieldset>
          <LabelFaIcon className="icon-group" />
          <Label>
            <Translate id="company.employees_size" />
          </Label>
          <Input
            color={company.font_color_code}
            type="number"
            value={company.payroll_number || ""}
            min={0}
            onChange={this.onChangePayrollNumber}
          />
        </Fieldset>
        <Actions>
          <CancelButton type="button" onClick={this.onCancel}>
            <Translate id="company.buttons.cancel" />
          </CancelButton>
          <PrimaryButton type="submit">
            <Translate id="company.buttons.complete" />
          </PrimaryButton>
        </Actions>
      </Form>
    );
  }
}

const Form = styled.form`
  height: 100%;
  width: 900px;
  padding-top: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Fieldset = styled.div`
  margin-bottom: 10px;
  padding: 20px 0;
  margin: 0 40px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f5f5f5;
  &:last-child {
    border-bottom: none;
  }
`;

const LabelIconBase = css`
  margin-right: 10px;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.4);
`;

const LabelIcon = styled(Icon)`
  ${LabelIconBase};
`;

const LabelFaIcon = styled.i`
  ${LabelIconBase};
`;

const Label = styled.label`
  width: 220px;
  flex-grow: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.4);
  cursor: auto;
`;

const Input = styled.input`
  flex-grow: 1;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.8);
  border: 1px solid transparent !important;
  &:focus {
    border: 1px solid
      ${(props: StyleProps) => {
        return props.color;
      }} !important;
  }
`;

const SelectField = styled.div`
  display: flex;
`;

const Select = styled.select`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.8);
  border-radius: 3px;
  &:focus {
    border: 1px solid
      ${(props: { color: string }) => {
        return props.color;
      }};
  }
`;

const DateSeparator = styled.div`
  margin: 0 5px;
  font-size: 13px;
`;

const Actions = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  padding: 40px;
`;

const CancelButton = LinkButton.extend`
  margin-right: 20px;
`;

export default EditBasicInfo;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/EditBasicInfo.tsx